// SVG overlay that draws stepped L-shaped dotted paths from each gauge
// corner to the centre bubble — matches the inverter LCD style.
//
// Self-measures its own container so it stays accurate on every viewport
// (mobile portrait, tablet, desktop).

import React, { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";

export default function FlowOverlay({
  bubbleSize = 80,
  gaugeSize = 160,
  online,
  solarKw,
  gridSigned,
  homeKw,
  offGrid,
}) {
  const ref = useRef(null);
  const [size, setSize] = useState({ w: 0, h: 0 });

  useEffect(() => {
    if (!ref.current || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(([entry]) => {
      setSize({ w: entry.contentRect.width, h: entry.contentRect.height });
    });
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);

  const W = size.w;
  const H = size.h;
  const ready = W > 0 && H > 0;

  return (
    <Box
      ref={ref}
      sx={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        zIndex: 1,
      }}
    >
      {ready && (
        <Paths
          W={W}
          H={H}
          bubbleSize={bubbleSize}
          gaugeSize={gaugeSize}
          online={online}
          solarKw={solarKw}
          gridSigned={gridSigned}
          homeKw={homeKw}
          offGrid={offGrid}
        />
      )}
    </Box>
  );
}

function Paths({
  W, H, bubbleSize, gaugeSize, online, solarKw, gridSigned, homeKw, offGrid,
}) {
  const cx = W / 2;
  const cy = H / 2;
  const half = gaugeSize / 2;
  const bubbleR = bubbleSize / 2;
  const off = bubbleR + 4;

  // Each cell of the 2×2 grid is W/2 × H/2.
  // Gauges are corner-aligned so their centres are at:
  const sx = half,        sy = half;
  const gx = W - half,    gy = half;
  const bx = half,        by = H - half;
  const hx = W - half,    hy = H - half;

  // Where each line attaches to the gauge — flush against the OUTER edge
  // of the gauge box (vertical centre), so the dotted line + arrow stop at
  // the border instead of running inside the gauge.
  const attach = half;
  const solarOut = { x: sx + attach, y: sy };
  const gridIn   = { x: gx - attach, y: gy };
  const batOut   = { x: bx + attach, y: by };
  const homeIn   = { x: hx - attach, y: hy };

  // Each arrow docks at its OWN corner of the bubble so the four paths
  // never overlap at the centre. Vertical offset ≈ 55% of the bubble radius
  // — places the docking just below/above its equator.
  const dy = bubbleR * 0.55;
  const bubbleUL = { x: cx - off, y: cy - dy }; // solar
  const bubbleLL = { x: cx - off, y: cy + dy }; // battery
  const bubbleUR = { x: cx + off, y: cy - dy }; // grid
  const bubbleLR = { x: cx + off, y: cy + dy }; // home

  // Build "right → vertical → right" stepped L-paths. The horizontal bend
  // happens midway between gauge edge and bubble.
  const stepFromGauge = (gauge, bubble) => {
    const midX = (gauge.x + bubble.x) / 2;
    return `M ${gauge.x} ${gauge.y} H ${midX} V ${bubble.y} H ${bubble.x}`;
  };
  const stepFromBubble = (bubble, gauge) => {
    const midX = (bubble.x + gauge.x) / 2;
    return `M ${bubble.x} ${bubble.y} H ${midX} V ${gauge.y} H ${gauge.x}`;
  };

  const solarPath = stepFromGauge(solarOut, bubbleUL);
  const gridPath  = stepFromBubble(bubbleUR, gridIn);
  const batPath   = stepFromGauge(batOut,  bubbleLL);
  const homePath  = stepFromBubble(bubbleLR, homeIn);

  // Active flags
  const solarActive = online && (solarKw || 0) > 0.05;
  const gridActive  = online && !offGrid && Math.abs(gridSigned || 0) > 0.05;
  const homeActive  = online && (homeKw || 0) > 0.05;
  const exporting   = (gridSigned || 0) < -0.05;

  const dim     = "#cfd5de";
  const solarC  = solarActive ? "#22c55e" : dim;
  const gridC   = gridActive ? (exporting ? "#22c55e" : "#ee5b29") : dim;
  const homeC   = homeActive ? "#ee5b29" : dim;

  return (
    <svg
      width={W}
      height={H}
      viewBox={`0 0 ${W} ${H}`}
      style={{ overflow: "visible", display: "block" }}
    >
      <DottedPath d={solarPath} color={solarC} active={solarActive} direction="forward" />
      {solarActive && <Triangle x={bubbleUL.x} y={bubbleUL.y} rotation={0} color={solarC} />}

      <DottedPath
        d={gridPath}
        color={gridC}
        active={gridActive}
        direction={exporting ? "forward" : "reverse"}
      />
      {gridActive && (
        exporting
          ? <Triangle x={gridIn.x} y={gridIn.y} rotation={0} color={gridC} />
          : <Triangle x={bubbleUR.x} y={bubbleUR.y} rotation={180} color={gridC} />
      )}

      <DottedPath d={batPath} color={dim} active={false} direction="forward" />

      <DottedPath d={homePath} color={homeC} active={homeActive} direction="forward" />
      {homeActive && <Triangle x={homeIn.x} y={homeIn.y} rotation={0} color={homeC} />}

      {/* Off-grid mark — positioned over the grid path */}
      {offGrid && (() => {
        const labelX = (bubbleUR.x + gridIn.x) / 2;
        return (
          <g>
            <text
              x={labelX}
              y={gy - 22}
              textAnchor="middle"
              fill="#bb0000"
              fontWeight="700"
              fontSize="11"
              fontFamily="Inter, sans-serif"
            >
              Off grid
            </text>
            <g
              transform={`translate(${labelX - 6} ${gy - 14})`}
              stroke="#bb0000"
              strokeWidth="2.5"
              strokeLinecap="round"
            >
              <line x1="0" y1="0" x2="12" y2="12" />
              <line x1="12" y1="0" x2="0" y2="12" />
            </g>
          </g>
        );
      })()}
    </svg>
  );
}

function DottedPath({ d, color, active, direction }) {
  return (
    <path
      d={d}
      fill="none"
      stroke={color}
      strokeWidth="2.5"
      strokeDasharray="4 6"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={
        active
          ? direction === "reverse" ? "flow-reverse" : "flow-forward"
          : ""
      }
    />
  );
}

function Triangle({ x, y, rotation = 0, color }) {
  return (
    <g
      transform={`translate(${x} ${y}) rotate(${rotation})`}
      style={{ filter: `drop-shadow(0 0 4px ${color}80)` }}
    >
      <polygon points="-2,-6 8,0 -2,6" fill={color} />
    </g>
  );
}
